import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { type SeverityType } from '@kirin/api-client/contracts/shared/severity';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { type JSX, useEffect, useRef, useState } from 'react';
import { EmptyState } from './EmptyState';
import { PatternCard, type PatternRowStatus } from './PatternCard';
import { RegexEditComponent, type RuleFormValues } from './RegexEditComponent';

/** Normalized rule shape exchanged with the parent (no client id). */
export interface NormalizedPattern {
  pattern: string;
  description: string;
  severity: SeverityType;
}

/** Internal row: a normalized rule plus an ephemeral client id for tracking. */
interface PatternRule extends NormalizedPattern {
  id: string;
  /** Pending deletion: kept visible briefly with a "Deleted" chip, but excluded
   *  from the emitted (persisted) value so it can't be resurrected. */
  pendingDelete?: boolean;
}

export interface PolicyPatternModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policyName: string;
  policyDescription: string;
  patterns: NormalizedPattern[];
  /** Single mutation channel — emitted on add / edit / delete. */
  onPatternsChange: (patterns: NormalizedPattern[]) => void;
  disabled?: boolean;
  readOnly?: boolean;
  /**
   * Support role: rows stay viewable and can enter edit mode (Pencil is
   * clickable, edit-drawer fields stay interactive), but the row's commit
   * actions — the "Edit Rule"/"Add Rule" submit and Delete — are disabled.
   * Nothing here reaches the backend regardless (the outer policy Save is
   * separately hidden for support), so this is purely a viewing affordance.
   */
  isSupportReadOnly?: boolean;
}

const PREVIEW_COUNT = 3;
// Transient indicator durations (KIR-2772 spec).
const NEW_CHIP_MS = 2000;
const EDITED_CHIP_MS = 1000;
const DELETED_CHIP_MS = 2000;

export function PolicyPatternModal({
  open,
  onOpenChange,
  policyName,
  policyDescription,
  patterns,
  onPatternsChange,
  disabled = false,
  readOnly = false,
  isSupportReadOnly = false,
}: PolicyPatternModalProps): JSX.Element {
  const [rows, setRows] = useState<PatternRule[]>([]);
  const [statuses, setStatuses] = useState<Record<string, PatternRowStatus>>(
    {}
  );
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Owner/manager: fully unrestricted.
  const allowEditing = !disabled && !readOnly;
  // Owner/manager OR support: can see the Pencil/Trash icons, enter a row's
  // edit mode, and open the "Add Rule" form. Support's actual commit actions
  // (Add/Edit submit, Delete) are separately disabled below via `isSupportOnly`.
  const canViewRowActions = allowEditing || (!disabled && isSupportReadOnly);
  const isSupportOnly = !allowEditing && canViewRowActions;

  const idCounter = useRef(0);
  const wasOpenRef = useRef(false);
  const rowsRef = useRef<PatternRule[]>(rows);
  const editFormRef = useRef<HTMLDivElement>(null);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );

  const newId = (): string => `rule-${idCounter.current++}`;

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  // Snapshot the incoming patterns into editable rows when the modal opens.
  // While open, `rows` is the source of truth so transient ids/statuses persist.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setRows(
        patterns.map(p => ({
          id: `rule-${idCounter.current++}`,
          ...p,
        }))
      );
      setStatuses({});
      setEditingId(null);
      setIsAdding(false);
      setIsExpanded(false);
    }
    wasOpenRef.current = open;
  }, [open, patterns]);

  // When an Edit drawer opens, scroll it into view inside the list container so
  // the user doesn't have to hunt for it (KIR-3344). Keyboard focus is handled
  // by the `autoFocus` prop on the drawer's Rule input.
  useEffect(() => {
    if (editingId && editFormRef.current) {
      editFormRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [editingId]);

  // Clear pending timers on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(timer => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const emit = (next: PatternRule[]): void => {
    onPatternsChange(
      next
        .filter(r => !r.pendingDelete)
        .map(({ pattern, description, severity }) => ({
          pattern,
          description,
          severity,
        }))
    );
  };

  const clearStatus = (id: string): void => {
    setStatuses(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const flashStatus = (
    id: string,
    status: PatternRowStatus,
    ms: number,
    onDone?: () => void
  ): void => {
    const existing = timersRef.current.get(id);
    if (existing) {
      clearTimeout(existing);
    }
    setStatuses(prev => ({ ...prev, [id]: status }));
    const timer = setTimeout(() => {
      timersRef.current.delete(id);
      if (onDone) {
        onDone();
      } else {
        clearStatus(id);
      }
    }, ms);
    timersRef.current.set(id, timer);
  };

  const handleAdd = (values: RuleFormValues): void => {
    // New rules are inserted at the TOP (Product decision, KIR-2772) so a
    // just-added rule is immediately visible above the "Show More" fold and its
    // green "New" highlight is seen. Existing rules keep their stored order.
    const row: PatternRule = { id: newId(), ...values };
    const next = [row, ...rowsRef.current];
    setRows(next);
    emit(next);
    setIsAdding(false);
    flashStatus(row.id, 'new', NEW_CHIP_MS);
  };

  const handleEditSave = (id: string, values: RuleFormValues): void => {
    const next = rowsRef.current.map(r =>
      r.id === id ? { ...r, ...values } : r
    );
    setRows(next);
    emit(next);
    setEditingId(null);
    flashStatus(id, 'edited', EDITED_CHIP_MS);
  };

  const handleDelete = (id: string): void => {
    // Mark pending-delete: keep the row visible for the grace window with a
    // "Deleted" chip, but flag it so emit() excludes it from the persisted
    // value. Flagging (rather than just emitting a filtered array) prevents an
    // add/edit during the window — which rebuilds from rowsRef — from
    // resurrecting the deleted rule.
    const next = rowsRef.current.map(r =>
      r.id === id ? { ...r, pendingDelete: true } : r
    );
    setRows(next);
    emit(next);
    if (editingId === id) {
      setEditingId(null);
    }
    flashStatus(id, 'deleted', DELETED_CHIP_MS, () => {
      setRows(rowsRef.current.filter(r => r.id !== id));
      clearStatus(id);
    });
  };

  const handleOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen) {
      setIsAdding(false);
      setEditingId(null);
      setIsExpanded(false);
    }
    onOpenChange(nextOpen);
  };

  const visibleRows = isExpanded ? rows : rows.slice(0, PREVIEW_COUNT);
  const hasMore = rows.length > PREVIEW_COUNT;
  // Only an editing row that is actually rendered should drive the dimming —
  // otherwise (e.g. editing a folded row, then "Show Less") `editingId` would
  // dim every visible row with no bright/active row and no drawer shown.
  const editingRowVisible =
    editingId !== null && visibleRows.some(r => r.id === editingId);

  // Collapsing can hide the row being edited; close that editor so `editingId`
  // isn't stranded (which would dim every row and orphan the drawer).
  const toggleExpanded = (): void => {
    const next = !isExpanded;
    setIsExpanded(next);
    if (
      !next &&
      editingId !== null &&
      !rows.slice(0, PREVIEW_COUNT).some(r => r.id === editingId)
    ) {
      setEditingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[70vh] flex flex-col"
        // Don't auto-focus a rule row's action button on open (that would reveal
        // its hover/focus icons immediately); let focus rest on the dialog.
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{policyName}</DialogTitle>
          <DialogDescription>{policyDescription}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 flex-1 min-h-0">
          {/* Scrollable rule list */}
          <div className="flex-1 overflow-y-auto pr-2 space-y-4">
            {rows.length > 0 && (
              <div className="space-y-2">
                {visibleRows.map(row => {
                  const isEditingRow =
                    canViewRowActions && editingId === row.id;

                  // While editing, the row becomes the header of a single
                  // connected panel whose "Edit rule" form extends directly
                  // beneath it (KIR-3344) — the row keeps showing which rule is
                  // being edited, with its hover edit/delete actions hidden. The
                  // other rows are dimmed to keep focus on the one being edited;
                  // while the Add Rule form is open, every existing row dims to
                  // keep focus on the new-rule form.
                  const isDimmed =
                    canViewRowActions &&
                    (isAdding || (editingRowVisible && !isEditingRow));
                  const onEdit = (): void => {
                    setIsAdding(false);
                    setEditingId(row.id);
                  };

                  return (
                    <div
                      key={row.id}
                      className={cn(
                        'transition-opacity',
                        isDimmed && 'opacity-40'
                      )}
                    >
                      {isEditingRow ? (
                        <div
                          ref={editFormRef}
                          className="overflow-hidden rounded-md border border-primary/50 bg-muted/20 ring-1 ring-primary/30"
                        >
                          <PatternCard
                            description={row.description}
                            severity={row.severity}
                            status={statuses[row.id]}
                            onEdit={onEdit}
                            onDelete={() => handleDelete(row.id)}
                            showActions={false}
                            bare
                          />
                          <div className="border-t border-primary/20 px-4 pb-4 pt-3">
                            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                              Edit rule
                            </p>
                            <RegexEditComponent
                              autoFocus
                              initialValues={{
                                pattern: row.pattern,
                                description: row.description,
                                severity: row.severity,
                              }}
                              submitLabel="Edit Rule"
                              onSubmit={values =>
                                handleEditSave(row.id, values)
                              }
                              onCancel={() => setEditingId(null)}
                              submitDisabled={isSupportOnly}
                            />
                          </div>
                        </div>
                      ) : (
                        <PatternCard
                          description={row.description}
                          severity={row.severity}
                          status={statuses[row.id]}
                          onEdit={onEdit}
                          onDelete={() => handleDelete(row.id)}
                          showActions={canViewRowActions}
                          deleteDisabled={isSupportOnly}
                        />
                      )}
                    </div>
                  );
                })}

                {hasMore && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={toggleExpanded}
                    className="w-full"
                  >
                    {isExpanded ? (
                      <>
                        <ChevronUp className="h-4 w-4 mr-2" />
                        Show Less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4 mr-2" />
                        Show {rows.length - PREVIEW_COUNT} More
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}

            {rows.length === 0 && !isAdding && (
              <EmptyState canAddPatterns={canViewRowActions} />
            )}
          </div>

          {/* Add rule section — hidden when disabled entirely; visible-but-disabled
              only for true read-only (not support, which can open the form but not
              submit it — see isSupportOnly on the RegexEditComponent below). */}
          {!disabled && (
            <div className="border-t pt-4">
              {canViewRowActions && isAdding ? (
                <div className="border rounded-md p-4 bg-muted/20">
                  <RegexEditComponent
                    submitLabel="Add Rule"
                    onSubmit={handleAdd}
                    onCancel={() => setIsAdding(false)}
                    submitDisabled={isSupportOnly}
                  />
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setEditingId(null);
                    setIsAdding(true);
                  }}
                  disabled={!canViewRowActions}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Rule
                </Button>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
